import api from "@/lib/axiosConfig";
import { z } from "zod";
import { baseRoute } from "./tirage.api";

// Schema for dimension fields (JSON structure)
const dimensionSchema = z
  .object({
    cm_width: z.number().optional(),
    in_width: z.number().optional(),
    cm_height: z.number().optional(),
    in_height: z.number().optional(),
  })
  .optional();

// Schema for currency fields (can be JSON string or object)
const currencySchema = z.union([
  z.string(), // JSON string format
  z.object({
    currency: z.string().optional(),
    amount: z.string().optional(),
  }),
]).optional();

// Schema for multilingual fields (JSON structure)
const multilingualSchema = z.record(z.string()).optional();

export const tirageSchema = z.object({
  id: z.string().optional(),
  tirageDescriptionNumeroEnregistrement: z
    .string()
    .nonempty("form.error.emptyField"),
  tirageDescriptionNumeroInventaire: z.string().optional(),
  tirageDescriptionNumeroAutre: z.string().optional(),
  tirageDescriptionNumeroAutrePublished: z.boolean().optional(),
  tirageDescriptionNumeroAncien: z.string().optional(),
  tirageDescriptionNumeroAncienPublished: z.boolean().optional(),
  tirageDescriptionDateCreation: z.string().optional(),
  tirageDescriptionDateCreationInconnue: z.boolean().optional(),
  tirageDescriptionDateCreationIncertaine: z.boolean().optional(),
  tirageDescriptionDateCreationEstimee: z.boolean().optional(),
  tirageDescriptionNotes: z.string().optional(),
  tirageDescriptionDimensionsWithMargins: dimensionSchema,
  tirageDescriptionDimensionsWithoutMargins: dimensionSchema,
  tirageDescriptionTechnicalNotes: z.string().optional(),
  tirageDescriptionConditionNotes: z.string().optional(),
  tiragePublicationStatus: z.string().optional(),
  tirageStatus: z.string().optional(),
  tirageDescriptionType: multilingualSchema,
  tirageDescriptionPrinter: multilingualSchema,
  tirageDescriptionTechnique: multilingualSchema,
  tirageDescriptionSupport: multilingualSchema,
  tirageDescriptionEditionNumber: multilingualSchema,
  tirageDescriptionSignatureInscription: multilingualSchema,
  tirageDescriptionSignatureInscriptionDos: multilingualSchema,
  tirageDescriptionSignatureInscriptionDosPublished: z.record(z.boolean()).optional(),
  tirageEstimationDescription: z.string().optional(),
  tirageEstimationAmount: currencySchema,
  tirageSaleDescription: z.string().optional(),
  tirageSaleAmount: currencySchema,
  tirageAcquisitionDescription: z.string().optional(),
  tirageAcquisitionAmount: currencySchema,
  tirageProductionDescription: z.string().optional(),
  tirageProductionAmount: currencySchema,
  tirageEncadrementDescription: z.string().optional(),
  tirageEncadrementAmount: currencySchema,
  tirageAssuranceDescription: z.string().optional(),
  tirageAssuranceAmount: currencySchema,
  tirageStorageLocation1: z.string().optional(),
  tirageStorageLocation2: z.string().optional(),
  tirageStorageLocation3: z.string().optional(),
});

export const tirageSchemaUpdate = tirageSchema.pick({
  id: true,
  tirageDescriptionNumeroEnregistrement: true,
});

export const appendNoDuplicateRecordNumberRefine = <
  T extends {
    id?: string;
    tirageDescriptionNumeroEnregistrement: string;
  },
>(
  schema: z.ZodType<T>,
) =>
  schema.refine(
    async (val) =>
      await validateNoDuplicateRecordNumbers(
        val.tirageDescriptionNumeroEnregistrement,
        val.id,
      )
        .then(() => true)
        .catch(() => false),
    {
      message: "form.error.tirage.duplicateRecordNumbers",
      path: ["tirageDescriptionNumeroEnregistrement"],
    },
  );

export async function validateNoDuplicateRecordNumbers(
  recordNumber: string,
  id?: string,
) {
  await api.get<void>(`${baseRoute}/record-number/validate/no-duplicates`, {
    params: {
      value: recordNumber,
      id,
    },
  });
}
